import React from 'react';
import classNames from 'classnames';
import { FormattedMessage } from '../../../utils/i18n';
import { Input, DateRangeSelect, DateQuickRangeOneWay } from '../inputs';
import { DisclaimerTooltip } from '../tooltips';
import { getMaxEndDate } from '@/utils/dates';
import PERIOD_END_DATA_OPTIONS from '@/types/period-end-data-options';
import { TIME_PERIODS } from '@/utils/constants';
import Nullable from '@/types/nullable';
import Locale from '@/types/locale';
import Disclaimer from '@/types/disclaimer';

type HeaderOptions = {
  hideTargetRate?: boolean;
  hideDateRange?: boolean;
  hideSegmentedControl?: boolean;
};

type ChartWarning = {
  key: string;
  data: {
    list: string[];
  };
};

type Props = {
  chartWarning: Nullable<ChartWarning>;
  dark?: boolean;
  dateQuickRange: TIME_PERIODS;
  dateQuickRangeValues: TIME_PERIODS[];
  endDate: Date;
  headerOptions?: HeaderOptions;
  locale: Locale;
  monthEndDataOption?: PERIOD_END_DATA_OPTIONS;
  onChangeTargetROI: (value: string) => void;
  onDateRangeChange: (start: any, end: any, isYTD?: boolean) => void;
  onQuickDateChange: (val: any) => void;
  readOnly?: boolean;
  showInlineTitle?: boolean;
  startDate: Date | null;
  targetROI?: number;
};

const ChartToolBar: React.FC<Props> = ({
  chartWarning,
  dark,
  dateQuickRange,
  dateQuickRangeValues,
  endDate,
  headerOptions,
  locale,
  onChangeTargetROI,
  onDateRangeChange,
  onQuickDateChange,
  readOnly,
  showInlineTitle,
  startDate,
  targetROI,
  monthEndDataOption,
}) => {
  const targetROIClasses = classNames('target-rate', 'd-flex', 'align-items-center', {
    print: !!targetROI,
  });

  const renderTargetRate = () => (
    <div className={targetROIClasses}>
      <label>
        <FormattedMessage id="Charts.Growth.TargetROI" />
      </label>
      <Input
        readOnly={readOnly}
        className="target-roi"
        dark={dark}
        compact
        type="number"
        onValueChange={onChangeTargetROI}
        value={targetROI}
        invert={dark}
        rightSection={
          <span>
            <span className="print-only">{targetROI}</span> %
          </span>
        }
      />
    </div>
  );

  const renderSegmentedControl = () => (
    <div className="no-print date-quick-range-picker">
      <DateQuickRangeOneWay selected={dateQuickRange} onChange={onQuickDateChange} dark={dark} values={dateQuickRangeValues} darkerBackground={dark} />
    </div>
  );

  const renderDateRange = () => (
    <DateRangeSelect
      className="print"
      startDate={startDate}
      endDate={endDate}
      onChange={onDateRangeChange}
      maxDate={getMaxEndDate(monthEndDataOption)}
      dark={dark}
      locale={locale}
      readOnly={readOnly}
    />
  );

  const renderChartWarning = () =>
    chartWarning ? (
      <div className="toolbar-icons d-flex align-items-center">
        <DisclaimerTooltip className="toolbar-disclaimer" disclaimer={chartWarning as Disclaimer} />
      </div>
    ) : null;

  const showTargetRate = !headerOptions?.hideTargetRate;
  const showSegmentedControl = !headerOptions?.hideSegmentedControl;
  const showDateRange = !headerOptions?.hideDateRange;

  if (showSegmentedControl && !showTargetRate && !showDateRange && !chartWarning) {
    return <div className={classNames('chart-toolbar-container toolbar', { 'inline-toolbar-container': showInlineTitle })}>{renderSegmentedControl()}</div>;
  }

  return (
    <div className={classNames('chart-toolbar-container', { 'inline-toolbar-container': showInlineTitle })}>
      <div className="toolbar d-flex justify-content-end align-items-center">
        <div className={classNames('options-container d-flex align-items-center', { print: !!targetROI })}>
          {showTargetRate && renderTargetRate()}
          {showSegmentedControl && renderSegmentedControl()}
        </div>
        <div className="toolbar-date-warning">
          {showDateRange && renderDateRange()}
          {renderChartWarning()}
        </div>
      </div>
    </div>
  );
};

export default ChartToolBar;
